/**
 * Google Analytics (GA4) Integration Utility
 * Handles dynamic script loading and accurate SPA (Single Page Application) virtual pageview tracking.
 */

declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || "G-7VXYF618JZ";

/**
 * Checks whether analytics cookie tracking is allowed (active unless explicitly declined by the user).
 */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const consent = localStorage.getItem("cookie_consent");
    const choice = localStorage.getItem("cookie_consent_choice");
    if (consent === "false" || choice === "declined") {
      return false;
    }
    return true;
  } catch {
    return true;
  }
}

/**
 * Deletes Google Analytics (_ga, _gid, _gat) cookies from browser storage when consent is declined or withdrawn.
 */
function clearAnalyticsCookies() {
  if (typeof document === "undefined") return;
  try {
    const cookies = document.cookie.split(";");
    const hostname = window.location.hostname;
    const domainParts = hostname.split(".");
    const rootDomain = domainParts.length >= 2 ? `.${domainParts.slice(-2).join(".")}` : hostname;

    for (const cookie of cookies) {
      const eqPos = cookie.indexOf("=");
      const name = (eqPos > -1 ? cookie.substring(0, eqPos) : cookie).trim();
      if (name.startsWith("_ga") || name.startsWith("_gid") || name.startsWith("_gat")) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${hostname};`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${rootDomain};`;
      }
    }
  } catch {
    // Ignore cookie deletion errors in restricted environments
  }
}

let lastSetUserId = "";
let lastTrackedPath = "";

/**
 * Initializes Google Analytics dynamically if a Measurement ID is provided.
 * Ensures the script is injected only once and configures Consent Mode v2.
 */
export function initGA() {
  if (typeof window === "undefined" || !MEASUREMENT_ID) {
    console.log("[Analytics] No GA Measurement ID found, tracking disabled.");
    return;
  }

  // Check if already loaded
  if (window.gtag) return;

  try {
    // 1. Setup the global dataLayer and gtag function FIRST (before script loads)
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };

    // 2. Consent Mode v2:
    // Analytics storage is enabled by default unless the user clicked "Reddet" (Decline),
    // ensuring GA4 does not drop users overnight or split pre-consent / post-consent sessions.
    // Advertising & profiling storage remain permanently 'denied'.
    const isGranted = hasAnalyticsConsent();
    const defaultAnalyticsStatus = isGranted ? "granted" : "denied";

    if (!isGranted) {
      clearAnalyticsCookies();
    }

    window.gtag("consent", "default", {
      analytics_storage: defaultAnalyticsStatus,
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });

    // Redact ads data and disable URL passthrough
    window.gtag("set", "ads_data_redaction", true);
    window.gtag("set", "url_passthrough", false);

    window.gtag("js", new Date());

    // 3. Configure GA4 with privacy safeguards (IP anonymization, no Google Signals profiling)
    const isIframe = window.self !== window.top;
    const configOptions: Record<string, any> = {
      send_page_view: false, // We manually track virtual page views via standard 'page_view' event
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    };
    if (isIframe) {
      configOptions.cookie_flags = "SameSite=None;Secure";
    }

    if (isGranted) {
      try {
        let persistentId = localStorage.getItem("persistent_player_id") || "";
        if (!persistentId) {
          persistentId = "usr_" + Math.random().toString(36).substring(2, 10);
          localStorage.setItem("persistent_player_id", persistentId);
        }
        if (persistentId) {
          lastSetUserId = persistentId;
          configOptions.user_id = persistentId;
          window.gtag("set", { user_id: persistentId });
        }
      } catch {
        // Ignore storage errors
      }
    }

    window.gtag("config", MEASUREMENT_ID, configOptions);

    // 4. Defer injecting the Google Tag (gtag.js) script until after initial page render / interaction
    // while dataLayer queues all initial events immediately.
    let scriptInjected = false;
    const injectGtagScript = () => {
      if (scriptInjected) return;
      scriptInjected = true;
      try {
        const script = document.createElement("script");
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
        document.head.appendChild(script);
      } catch {}
    };

    const onFirstInteraction = () => {
      injectGtagScript();
      window.removeEventListener("pointerdown", onFirstInteraction, true);
      window.removeEventListener("keydown", onFirstInteraction, true);
      window.removeEventListener("touchstart", onFirstInteraction, true);
      window.removeEventListener("scroll", onFirstInteraction, true);
    };

    window.addEventListener("pointerdown", onFirstInteraction, { capture: true, passive: true, once: true });
    window.addEventListener("keydown", onFirstInteraction, { capture: true, passive: true, once: true });
    window.addEventListener("touchstart", onFirstInteraction, { capture: true, passive: true, once: true });
    window.addEventListener("scroll", onFirstInteraction, { capture: true, passive: true, once: true });

    const scheduleAfterLoad = () => {
      const w = window as any;
      if (typeof w.requestIdleCallback === "function") {
        w.requestIdleCallback(() => injectGtagScript(), { timeout: 1500 });
      } else {
        setTimeout(injectGtagScript, 800);
      }
    };

    if (document.readyState === "complete") {
      scheduleAfterLoad();
    } else {
      window.addEventListener("load", scheduleAfterLoad, { once: true });
      setTimeout(injectGtagScript, 3000);
    }
  } catch (error) {
    console.error("[Analytics] Error loading Google Analytics script:", error);
  }
}

/**
 * Tracks a virtual page view in the SPA using the official GA4 'page_view' event.
 */
export function trackPageView(path: string, title: string) {
  if (typeof window === "undefined") return;

  try {
    document.title = title;

    const normalizedPath = path || "/";
    if (normalizedPath === lastTrackedPath) {
      return;
    }
    lastTrackedPath = normalizedPath;

    if (window.gtag && MEASUREMENT_ID) {
      const pageLocation = window.location.origin + normalizedPath;
      window.gtag("event", "page_view", {
        page_title: title,
        page_location: pageLocation,
        page_path: normalizedPath,
        send_to: MEASUREMENT_ID,
      });
      if (process.env.NODE_ENV !== "production") {
        console.log(`[Analytics] Virtual Page View Tracked: ${normalizedPath} (${title})`);
      }
    }
  } catch (error) {
    console.warn("[Analytics] Failed to track page view:", error);
  }
}

/**
 * Tracks custom events like starting a game or joining a table.
 */
export function trackEvent(action: string, category?: string, label?: string, value?: number) {
  if (typeof window === "undefined" || !window.gtag || !MEASUREMENT_ID) return;

  try {
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  } catch (error) {
    console.warn("[Analytics] Failed to track event:", error);
  }
}

/**
 * Updates Consent Mode status dynamically when the user makes a choice (Accept / Decline).
 * Ad storage and personalization are always kept 'denied' in accordance with our Cookie Policy.
 */
export function updateConsent(granted: boolean) {
  if (typeof window === "undefined" || !window.gtag) return;

  const analyticsStatus = granted ? "granted" : "denied";
  try {
    window.gtag("consent", "update", {
      analytics_storage: analyticsStatus,
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });

    if (granted) {
      let persistentId = "";
      try {
        persistentId = localStorage.getItem("persistent_player_id") || "";
        if (!persistentId) {
          persistentId = "usr_" + Math.random().toString(36).substring(2, 10);
          localStorage.setItem("persistent_player_id", persistentId);
        }
      } catch {
        // Ignore
      }
      if (persistentId) {
        setUserId(persistentId);
      }
    } else {
      // User declined or withdrew consent: remove persistent tracking ID and delete _ga cookies
      lastSetUserId = "";
      try {
        localStorage.removeItem("persistent_player_id");
      } catch {
        // Ignore
      }
      window.gtag("set", { user_id: null });
      window.gtag("config", MEASUREMENT_ID, {
        user_id: null,
        send_page_view: false,
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      clearAnalyticsCookies();
    }

    if (process.env.NODE_ENV !== "production") {
      console.log(`[Analytics] Consent mode updated dynamically to: ${analyticsStatus}`);
    }
  } catch (error) {
    console.warn("[Analytics] Failed to update consent mode:", error);
  }
}

/**
 * Sets the pseudonymous User-ID for cross-session tracking when analytics consent is active.
 */
export function setUserId(userId: string) {
  if (typeof window === "undefined" || !window.gtag || !MEASUREMENT_ID) return;
  if (!hasAnalyticsConsent()) return;
  if (!userId || userId === lastSetUserId) return;

  lastSetUserId = userId;
  try {
    window.gtag("set", {
      user_id: userId,
    });
    window.gtag("config", MEASUREMENT_ID, {
      user_id: userId,
      send_page_view: false,
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    if (process.env.NODE_ENV !== "production") {
      console.log(`[Analytics] User-ID set: ${userId}`);
    }
  } catch (error) {
    console.warn("[Analytics] Failed to set user ID:", error);
  }
}

